import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../lib/api';
import { friendlyAuthError } from '../lib/authErrors';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowRight, UserPlus, LogIn } from 'lucide-react';
import { motion } from 'framer-motion';
import Globe3D from '../components/Globe3D';
import TurnstileWidget from '../components/TurnstileWidget';
import InstallAppBanner from '../components/InstallAppBanner';
import AgeGateModal from '../components/AgeGateModal';
import SocialAuthButtons from '../components/SocialAuthButtons';
import { adTrackRegistration } from '../lib/adPixels';
import { suggestEmailFix } from '../lib/emailTypo';

// v6.x — Sign-in globe is a SMALLER VERSION of the title screen globe.
// Same 5-mask orbital config as SplashScreen's ORBITING_MASKS, scaled by
// ONE uniform ratio: auth globe px / splash globe px (120/280).
const AUTH_GLOBE_SIZE = 120;
const AUTH_RATIO = AUTH_GLOBE_SIZE / 280;
const AUTH_MASKS = [
  { orbitRadius: 230 * AUTH_RATIO, speed: 6,  startAngle: 0,   tilt: 0,   size: 78 * AUTH_RATIO },
  { orbitRadius: 220 * AUTH_RATIO, speed: 9,  startAngle: 72,  tilt: 50,  size: 66 * AUTH_RATIO },
  { orbitRadius: 220 * AUTH_RATIO, speed: 12, startAngle: 144, tilt: -50, size: 66 * AUTH_RATIO },
  { orbitRadius: 225 * AUTH_RATIO, speed: 8,  startAngle: 216, tilt: 25,  size: 72 * AUTH_RATIO },
  { orbitRadius: 225 * AUTH_RATIO, speed: 10, startAngle: 288, tilt: -25, size: 72 * AUTH_RATIO },
];

function OrbitingMaskAuth({ mask }) {
  const ref = useRef(null);
  useEffect(() => {
    const start = performance.now();
    let raf;
    const animate = (now) => {
      const t = (now - start) / 1000;
      const angle = (mask.startAngle - (t / mask.speed) * 360) % 360;
      const rad = (angle * Math.PI) / 180;
      const tiltRad = (mask.tilt * Math.PI) / 180;
      const x = Math.cos(rad) * mask.orbitRadius;
      const z = Math.sin(rad) * mask.orbitRadius;
      const y = Math.sin(rad) * Math.sin(tiltRad) * mask.orbitRadius * 0.5;
      const zNorm = (z + mask.orbitRadius) / (2 * mask.orbitRadius);
      const opacity = 0.1 + zNorm * 0.9;
      if (ref.current) {
        ref.current.style.transform = `translate(${x}px, ${y}px) scale(${0.6 + zNorm * 0.5})`;
        ref.current.style.opacity = opacity;
        ref.current.style.zIndex = z > 0 ? 3 : 1;
      }
      raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [mask]);
  return (
    <span ref={ref} className="absolute pointer-events-none" style={{
      left: '50%', top: '50%', marginLeft: -mask.size/2, marginTop: -mask.size/2,
      width: mask.size, height: mask.size,
      filter: 'drop-shadow(0 0 2px rgba(200,150,255,0.5))',
    }}>
      <img src="/masks.png" alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
    </span>
  );
}

export default function AuthPage() {
  const { login, verifyMfa, register, guestLogin } = useAuth();
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const emailSuggestion = suggestEmailFix(email);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // v6.x — Forgot-password sub-mode. When `mode === 'forgot'` the card
  // shows an email-only form that POSTs /auth/password-reset/request.
  // `forgotSent` flips to a generic "check your email" confirmation
  // (enumeration-safe — same copy whether or not the email exists).
  const [forgotSent, setForgotSent] = useState(false);
  // v5.7.80 — MFA challenge state. When the backend returns
  // {mfa_required: true, mfa_challenge_token}, we hide the password
  // form and show the MFA code entry. The challenge token has a
  // 5-minute server-side TTL, plenty for the user to fish out their
  // authenticator app.
  const [mfaChallenge, setMfaChallenge] = useState(null);  // {token, backup_codes_remaining}
  const [mfaCode, setMfaCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  // v5.7.52 — Turnstile token; null until widget completes its challenge.
  // When REACT_APP_TURNSTILE_SITE_KEY is unset the widget renders nothing
  // and `turnstileEnabled` stays false (so the submit button doesn't gate).
  const [turnstileToken, setTurnstileToken] = useState(null);
  // 2026-07 — reveal halo + earth + orbiting masks TOGETHER once the NASA
  // texture is on the sphere (prevents the halo-first flash). 1.5s fallback
  // so a slow texture fetch never hides the brand block for long.
  const [globeReady, setGlobeReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setGlobeReady(true), 1500);
    return () => clearTimeout(t);
  }, []);
  // Bumped after any rejected submit so <TurnstileWidget> re-runs its challenge
  // and yields a FRESH single-use token (avoids the stale-token dead-end).
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const turnstileEnabled = Boolean((process.env.REACT_APP_TURNSTILE_SITE_KEY || '').trim());
  // Graceful degrade (2026-07, testing-agent finding): if the Turnstile
  // script fails to load / never yields a token (blocked CDN, un-whitelisted
  // preview domain), don't strand the user on a permanently-disabled button.
  // After 8s without a token the frontend gate lifts — the BACKEND stays
  // authoritative (when TURNSTILE_SECRET_KEY is configured it still rejects
  // tokenless register/guest requests).
  const [turnstileTimedOut, setTurnstileTimedOut] = useState(false);
  useEffect(() => {
    if (!turnstileEnabled || turnstileToken) return undefined;
    const t = setTimeout(() => setTurnstileTimedOut(true), 8000);
    return () => clearTimeout(t);
  }, [turnstileEnabled, turnstileToken]);
  const turnstileGate = turnstileEnabled && !turnstileToken && !turnstileTimedOut;
  // Register + Guest need a fresh token; Login doesn't (no DoS risk there).
  const needsTurnstile = turnstileEnabled && (mode === 'register');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (needsTurnstile && !turnstileToken && !turnstileTimedOut) {
      setError('Please complete the verification challenge.');
      return;
    }
    // 2026-08-11 (user feedback) — confirm-password on signup.
    if (mode === 'register' && password !== confirmPassword) {
      setError("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      if (mode === 'login') {
        const res = await login(email, password);
        // v5.7.80 — Detect MFA challenge response. If present, swap the
        // form to the MFA-code entry. The cookies have NOT been set;
        // the user must complete the challenge to log in.
        if (res?.mfa_required) {
          setMfaChallenge({
            token: res.mfa_challenge_token,
            // v5.8.7 — backend now returns `has_backup_codes` (boolean)
            // instead of the exact count, to avoid leaking the count
            // pre-MFA-solved. Fallback to legacy `backup_codes_remaining`
            // for old backends during rollouts.
            hasBackupCodes: typeof res.has_backup_codes === 'boolean'
              ? res.has_backup_codes
              : (res.backup_codes_remaining || 0) > 0,
          });
          setLoading(false);
          return;
        }
      } else {
        await register(email, username, password, turnstileToken);
        // 2026-08 — ad-pixel conversion (consent-gated, no-op without IDs).
        adTrackRegistration('email');
      }
    } catch (err) {
      // v5.8.36 (Q1 follow-up) — Surface env-misconfig precisely
      // instead of the generic "Something went wrong" toast.
      if (err.misconfigured) {
        setError(err.message);
        setTurnstileToken(null);
        return;
      }
      // 2026-07 — transient infra failure (Cloudflare 52x / network /
      // timeout, after api.js retries are exhausted): show one calm
      // message instead of raw Cloudflare/axios text.
      const friendly = friendlyAuthError(err);
      const detail = err.response?.data?.detail;
      setError(friendly
        || (typeof detail === 'string' ? detail : (detail?.message || 'Something went wrong')));
      // Reset Turnstile so user can retry with a fresh token.
      setTurnstileToken(null);
      setTurnstileResetKey((k) => k + 1);
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const payload = useBackupCode
        ? { mfa_challenge_token: mfaChallenge.token, backup_code: mfaCode }
        : { mfa_challenge_token: mfaChallenge.token, code: mfaCode };
      await verifyMfa(payload);
      // On success, AuthContext sets the user — the App rerenders into MainApp.
    } catch (err) {
      const detail = err.response?.data?.detail;
      const code = detail?.code;
      setError(typeof detail === 'string' ? detail : (detail?.message || 'Invalid code'));
      // INVALID_CHALLENGE → kick back to password form (token expired)
      if (code === 'INVALID_CHALLENGE') {
        setMfaChallenge(null);
        setMfaCode('');
        setUseBackupCode(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authAPI.passwordResetRequest(email);
      setForgotSent(true);
    } catch (err) {
      // Enumeration-safe: only a rate-limit is surfaced; any other error
      // still shows the generic "check your email" so we never leak
      // whether the address is registered.
      if (err.response?.status === 429) {
        setError('Too many requests. Please wait a minute and try again.');
      } else {
        setForgotSent(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGuest = async () => {
    setError('');
    if (turnstileEnabled && !turnstileToken) {
      setError('Please complete the verification challenge above before continuing as guest.');
      return;
    }
    setLoading(true);
    try {
      await guestLogin(turnstileToken);
    } catch (err) {
      const friendly = friendlyAuthError(err);
      const detail = err.response?.data?.detail;
      setError(friendly
        || (typeof detail === 'string' ? detail : (detail?.message || 'Failed to create guest account')));
      setTurnstileToken(null);
      setTurnstileResetKey((k) => k + 1);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 bg-background" data-testid="auth-page">
      {/* One-time 18+ age gate — covers the guest path that skips the signup
          consent line, so every entry path affirms the 18+ minimum. */}
      <AgeGateModal />

      {/* v5.8.21 — Mobile-only install prompt on landing. Detects iOS/
          Android UA + 24h dismissal cooldown so we don't pester users
          who said "no thanks" once. Desktop visitors don't see it. */}
      <InstallAppBanner path={null} />

      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="relative z-10 w-full max-w-sm"
      >
        <div className="flex flex-col items-center mb-8">
          <div className="relative mb-3 flex items-center justify-center" style={{ width: 180, height: 180, perspective: '400px', transformStyle: 'preserve-3d', opacity: globeReady ? 1 : 0, transition: 'opacity 400ms ease' }}>
            {/* Atmospheric halo — SAME 13-stop ultra-thin bright limb as the
                title screen (SplashScreen.js), scaled 360→154px for the 120px
                globe. blur scaled 4px→2px to match. */}
            <div className="absolute" style={{
              width: 360 * AUTH_RATIO, height: 360 * AUTH_RATIO,
              borderRadius: '50%',
              background: 'radial-gradient(circle, transparent 0%, transparent 48%, rgba(210,245,255,0.88) 49.8%, rgba(195,238,252,0.72) 51%, rgba(178,230,248,0.58) 52.5%, rgba(160,220,242,0.45) 54%, rgba(142,208,235,0.34) 56%, rgba(122,195,228,0.25) 58.5%, rgba(102,180,218,0.18) 62%, rgba(82,162,208,0.12) 67%, rgba(62,142,195,0.078) 74%, rgba(45,122,180,0.048) 82%, rgba(30,102,165,0.026) 91%, transparent 100%)',
              filter: 'blur(2px)',
            }} />
            <div style={{ position: 'relative', zIndex: 2 }}>
              <Globe3D size={AUTH_GLOBE_SIZE} spinning={true} spinSpeed={0.8} brightness={0.9} baseSpeed={0.8} opaque={false} onReady={() => setGlobeReady(true)} />
            </div>
            {/* 3 Orbiting masks */}
            {AUTH_MASKS.map((m, i) => <OrbitingMaskAuth key={i} mask={m} />)}
          </div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
            Fantasy World Star
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Draft the world. Score the headlines.</p>
          <p className="text-[9px] text-muted-foreground mt-2">© 2026 Gazumba Games. All Rights Reserved. Fantasy World Star™</p>
        </div>

        <div className="bg-card border border-border/60 rounded-2xl p-6 shadow-sm">
          {/* v5.7.80 — MFA challenge step. Shown when /auth/login
              responds with mfa_required:true. The challenge token has
              a 5-min server-side TTL. Backup-code toggle lets users
              who lost their authenticator use a one-time code. */}
          {mfaChallenge ? (
            <form onSubmit={handleMfaSubmit} className="space-y-4" data-testid="auth-mfa-form">
              <h2 className="text-lg font-semibold">Two-Factor Authentication</h2>
              <p className="text-sm text-muted-foreground">
                {useBackupCode
                  ? 'Enter one of your saved backup codes.'
                  : 'Enter the 6-digit code from your authenticator app.'}
              </p>
              <Input
                type="text"
                placeholder={useBackupCode ? 'XXXXX-XXXXX' : '123456'}
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                inputMode={useBackupCode ? 'text' : 'numeric'}
                maxLength={useBackupCode ? 12 : 6}
                autoFocus
                required
                data-testid="auth-mfa-code-input"
              />
              {error && <p className="text-sm text-destructive" data-testid="auth-mfa-error" role="alert">{error}</p>}
              <Button
                type="submit"
                className="w-full"
                disabled={loading || !mfaCode}
                data-testid="auth-mfa-submit-button"
              >
                {loading ? 'Verifying...' : 'Verify'}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
              <div className="flex justify-between text-xs text-muted-foreground">
                <button
                  type="button"
                  className="underline"
                  onClick={() => { setUseBackupCode(!useBackupCode); setMfaCode(''); setError(''); }}
                  data-testid="auth-mfa-toggle-backup"
                >
                  {useBackupCode ? 'Use authenticator code' : 'Use a backup code'}
                </button>
                <button
                  type="button"
                  className="underline"
                  onClick={() => { setMfaChallenge(null); setMfaCode(''); setUseBackupCode(false); setError(''); }}
                  data-testid="auth-mfa-cancel"
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : mode === 'forgot' ? (
            <div data-testid="auth-forgot-form">
              {forgotSent ? (
                <div className="text-center space-y-3" data-testid="auth-forgot-sent">
                  <h2 className="text-lg font-semibold">Check your email</h2>
                  <p className="text-sm text-muted-foreground">
                    If an account exists for that email, we sent a link to reset your
                    password. The link expires in 1 hour.
                  </p>
                  <button
                    type="button"
                    className="text-sm underline text-muted-foreground"
                    onClick={() => { setMode('login'); setForgotSent(false); setError(''); }}
                    data-testid="auth-forgot-back"
                  >
                    Back to sign in
                  </button>
                </div>
              ) : (
                <form onSubmit={handleForgot} className="space-y-4">
                  <h2 className="text-lg font-semibold">Reset your password</h2>
                  <p className="text-sm text-muted-foreground">
                    Enter your account email and we&apos;ll send you a link to set a new password.
                  </p>
                  <Input
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="auth-forgot-email-input"
                  />
                  {error && <p className="text-sm text-destructive" data-testid="auth-forgot-error" role="alert">{error}</p>}
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={loading}
                    data-testid="auth-forgot-submit"
                  >
                    {loading ? 'Sending…' : 'Send reset link'}
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                  <button
                    type="button"
                    className="w-full text-xs text-muted-foreground underline"
                    onClick={() => { setMode('login'); setError(''); }}
                    data-testid="auth-forgot-cancel"
                  >
                    Back to sign in
                  </button>
                </form>
              )}
            </div>
          ) : (
          <>
          <div
            className="flex gap-2 mb-6"
            role="tablist"
            aria-label="Sign in or sign up"
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
                e.preventDefault();
                const next = mode === 'login' ? 'register' : 'login';
                setMode(next);
                requestAnimationFrame(() => document.querySelector(`[data-testid="auth-${next}-tab"]`)?.focus());
              }
            }}
          >
            <button
              onClick={() => setMode('login')}
              id="auth-tab-login"
              role="tab"
              aria-selected={mode === 'login'}
              aria-controls="auth-tabpanel"
              tabIndex={mode === 'login' ? 0 : -1}
              className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${
                mode === 'login' ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'
              }`}
              data-testid="auth-login-tab"
            >
              <LogIn className="h-4 w-4 inline mr-1" /> Sign In
            </button>
            <button
              onClick={() => setMode('register')}
              id="auth-tab-register"
              role="tab"
              aria-selected={mode === 'register'}
              aria-controls="auth-tabpanel"
              tabIndex={mode === 'register' ? 0 : -1}
              className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${
                mode === 'register' ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'
              }`}
              data-testid="auth-register-tab"
            >
              <UserPlus className="h-4 w-4 inline mr-1" /> Sign Up
            </button>
          </div>

          <form
            onSubmit={handleSubmit}
            className="space-y-4"
            role="tabpanel"
            id="auth-tabpanel"
            aria-labelledby={mode === 'login' ? 'auth-tab-login' : 'auth-tab-register'}
          >
            <Input
              type="email"
              placeholder="Email"
              aria-label="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="auth-email-input"
            />
            {/* 2026-09-17 — typo catcher ("…@gmail.con"): one tap to fix. */}
            {mode === 'register' && emailSuggestion && (
              <button
                type="button"
                className="-mt-2 text-left text-xs text-primary hover:underline underline-offset-2"
                onClick={() => setEmail(emailSuggestion)}
                data-testid="auth-email-suggestion"
              >
                Did you mean <span className="font-semibold">{emailSuggestion}</span>?
              </button>
            )}
            {mode === 'register' && (
              <Input
                type="text"
                placeholder="Username"
                aria-label="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                data-testid="auth-username-input"
              />
            )}
            <Input
              type="password"
              placeholder="Password"
              aria-label="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
              data-testid="auth-password-input"
            />
            {mode === 'register' && (
              <Input
                type="password"
                placeholder="Confirm password"
                aria-label="Confirm password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                data-testid="auth-confirm-password-input"
              />
            )}
            {error && <p className="text-sm text-destructive" data-testid="auth-error" role="alert">{error}</p>}
            {/* v5.7.52 — Turnstile shows on Register tab (and below for Guest).
                Renders nothing in dev when no site key is set. */}
            {(mode === 'register' || mode === 'login') && turnstileEnabled && mode === 'register' && (
              <TurnstileWidget onToken={setTurnstileToken} resetKey={turnstileResetKey} />
            )}
            <Button
              type="submit"
              className="w-full"
              disabled={loading || (needsTurnstile && !turnstileToken && !turnstileTimedOut)}
              data-testid={mode === 'login' ? 'auth-submit-login' : 'auth-submit-register'}
            >
              {loading ? 'Loading...' : mode === 'login' ? 'Sign In' : 'Create Account'}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            {/* v6.x — Forgot-password entry point (login mode only). */}
            {mode === 'login' && (
              <button
                type="button"
                className="w-full text-xs text-muted-foreground underline"
                onClick={() => { setMode('forgot'); setError(''); setForgotSent(false); }}
                data-testid="auth-forgot-password-link"
              >
                Forgot your password?
              </button>
            )}
            {/* v6.1.17.4 — Terms-of-Service acceptance text.  Apple App Store
                Guideline 3.1.2(a) + GDPR/CCPA require explicit consent text on
                account creation.  We surface it on every mode so guest + signin
                users see it too. */}
            {mode === 'register' && (
              <p
                className="text-[11px] text-muted-foreground text-center leading-relaxed"
                data-testid="auth-tos-acceptance"
              >
                By creating an account, you confirm you are 18 or older and agree to our{' '}
                <a
                  href="https://fantasyworldstar.com/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-2 hover:text-foreground"
                  data-testid="auth-terms-link"
                >
                  Terms of Service
                </a>{' '}
                and{' '}
                <a
                  href="https://fantasyworldstar.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-2 hover:text-foreground"
                  data-testid="auth-privacy-link"
                >
                  Privacy Policy
                </a>
                .
              </p>
            )}
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
            <div className="relative flex justify-center text-xs"><span className="bg-card px-2 text-muted-foreground">or</span></div>
          </div>

          {/* 2026-08-11 — social login (renders nothing until the backend
              reports a configured provider). */}
          <SocialAuthButtons
            onError={setError}
            onMfaRequired={(res) => setMfaChallenge({
              token: res.mfa_challenge_token,
              hasBackupCodes: typeof res.has_backup_codes === 'boolean'
                ? res.has_backup_codes
                : (res.backup_codes_remaining || 0) > 0,
            })}
          />

          {/* Guest needs its own Turnstile token when in Login mode (Register
              tab already renders one above and shares the same token). */}
          {turnstileEnabled && mode === 'login' && (
            <TurnstileWidget onToken={setTurnstileToken} resetKey={turnstileResetKey} />
          )}
          <Button
            variant="outline"
            className="w-full"
            onClick={handleGuest}
            disabled={loading || turnstileGate}
            data-testid="auth-guest-button"
          >
            Quick Play as Guest
          </Button>
          {/* 2026-09-07 (owner request) — mobile parity: browse the people
              catalogue (decade / nationality filters) without signing in. */}
          <a
            href="/people"
            className="block w-full text-center text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 mt-3 transition-colors"
            data-testid="auth-browse-people-link"
          >
            Or browse the people catalogue →
          </a>
          </>
          )}
        </div>

        {/* v6.8.4 — Persistent legal footer at the bottom of the login screen.
            Relative routes so they're environment-correct (preview/prod) and
            bypass the site gate; open in a new tab to preserve login context. */}
        <footer
          className="mt-6 flex items-center justify-center gap-3 text-xs text-muted-foreground"
          data-testid="auth-legal-footer"
        >
          <a
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2 hover:text-foreground transition-colors"
            data-testid="auth-footer-privacy-link"
          >
            Privacy Policy
          </a>
          <span aria-hidden="true" className="text-muted-foreground/40">·</span>
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2 hover:text-foreground transition-colors"
            data-testid="auth-footer-terms-link"
          >
            Terms of Service
          </a>
        </footer>
      </motion.div>
    </div>
  );
}
